import { useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Building2, ChevronsUpDown, KeyRound, LogOut, UserRound } from 'lucide-react'
import { toast } from 'sonner'
import type { Municipio } from '@/api/modelos'
import { cerrarSesionEnApi } from '@/api/auth'
import { limpiarSesion } from '@/sesion/almacen'
import CambioDeClave from '@/sesion/CambioDeClave'
import { ROLES } from '@/usuarios/etiquetas'
import { useSesion } from '@/sesion/useSesion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * La cabecera: municipio activo, usuario con su rol EN ESE municipio y salir.
 *
 * El selector de municipio aparece solo con dos o mas: un desplegable de una
 * sola opcion es ruido (la leccion del dashboard de Empresas). Cambiar de
 * municipio es cambiar de ruta, igual que en la API.
 */
export default function BarraUsuario({
  municipio = null,
  izquierda,
}: {
  municipio?: Municipio | null
  izquierda?: ReactNode
}) {
  const { sesion } = useSesion()
  const navegar = useNavigate()
  const [cambiandoClave, setCambiandoClave] = useState(false)

  const salir = useMutation({
    mutationFn: cerrarSesionEnApi,
    onError: () => {
      // El token igual se va de este equipo; el servidor lo caducara solo.
      toast.warning('No pudimos avisar al servidor, pero cerramos tu sesión en este equipo.')
    },
    onSettled: () => {
      limpiarSesion('salio')
      navegar('/login', { replace: true })
    },
  })

  const municipios = sesion?.municipios ?? []
  const usuario = sesion?.usuario

  return (
    // Fija arriba, como la barra lateral: si se fuera con el scroll, con la
    // barra lateral oculta el boton que la devuelve tambien desapareceria.
    // z-40 la deja por encima del mapa, cuyos paneles de Leaflet llegan a 800
    // pero viven dentro de un `isolate` (ver mapa/MapaDeDeudas.tsx).
    <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b bg-background px-4">
      {izquierda}

      <div className="flex min-w-0 items-center gap-2">
        <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        {municipios.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="ghost" size="sm" className="max-w-[16rem] truncate">
                  {municipio?.nombre ?? 'Elige un municipio'}
                  <ChevronsUpDown aria-hidden="true" />
                </Button>
              }
            />
            <DropdownMenuContent align="start">
              <DropdownMenuGroup>
                {/* La etiqueta es un GroupLabel de Base UI: sin Group, revienta. */}
                <DropdownMenuLabel>Cambiar de municipio</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {municipios.map((otro) => (
                  <DropdownMenuItem
                    key={otro.slug}
                    onClick={() => {
                      navegar(`/m/${otro.slug}`)
                    }}
                  >
                    {otro.nombre}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="truncate text-sm font-medium">
            {municipio?.nombre ?? 'Cobranza Municipal'}
          </span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {municipio !== null && municipio.roles.length > 0 && (
          <Badge variant="secondary" className="hidden sm:inline-flex">
            {municipio.roles
              .map((rol) => (rol in ROLES ? ROLES[rol as keyof typeof ROLES] : rol))
              .join(', ')}
          </Badge>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="outline" size="sm">
                <UserRound aria-hidden="true" />
                <span className="hidden max-w-[12rem] truncate sm:inline">
                  {usuario?.nombre ?? 'Mi cuenta'}
                </span>
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            <div className="px-1.5 py-1">
              <span className="block text-sm font-medium">{usuario?.nombre}</span>
              <span className="block text-xs text-muted-foreground">{usuario?.email}</span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setCambiandoClave(true)
              }}
            >
              <KeyRound aria-hidden="true" />
              Cambiar mi clave
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={salir.isPending}
              onClick={() => {
                salir.mutate()
              }}
            >
              <LogOut aria-hidden="true" />
              {salir.isPending ? 'Cerrando sesión…' : 'Cerrar sesión'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {usuario !== undefined && (
          <CambioDeClave
            email={usuario.email}
            abierto={cambiandoClave}
            alCerrar={() => {
              setCambiandoClave(false)
            }}
          />
        )}
      </div>
    </header>
  )
}
